import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { MapComponent } from '@components/entidades/common/map/map.component';
import { SignoCentroMedico } from 'src/app/interfaces/interface-bakend/entidades/signo-centro-medico.interface';
import { SignoCentroMedicoService } from 'src/app/services/entidades/signo-centros-medicos.service';
import { SignoLocalidad } from 'src/app/interfaces/interface-bakend/entidades/signo-localidad.interface';
import { SignoLocalidadService } from 'src/app/services/entidades/signo-localidades.service';
import { CatalogoService } from 'src/app/services/shared/catalogo.service';
import { Catalogo } from 'src/app/interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { SignoPlanService } from '@services/entidades/signo-planes.service';
import { SignoPlan } from '@interfaces/interface-bakend/entidades/signo-plan.interface';
import { environment } from '@env/environment';

@Component({
  selector: 'app-centro-medico-modificacion',
  templateUrl: './centro-medico-modificacion.component.html',
  styleUrls: ['./centro-medico-modificacion.component.scss']
})
export class CentroMedicoModificacionComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  signoCentroMedico: SignoCentroMedico;
  zonas: Catalogo[];
  localidades: SignoLocalidad[];
  rubros: Catalogo[];
  especialidades: Catalogo[];
  planes: SignoPlan[];

  // Para especialidades con chips
  especialidadesSeleccionadas: Catalogo[] = [];
  especialidadControl = new FormControl('');
  filteredEspecialidades: Catalogo[] = [];

  // Para rubros con chips
  rubrosSeleccionados: Catalogo[] = [];
  rubroControl = new FormControl('');
  filteredRubros: Catalogo[] = [];

  // Separadores para chips (Enter y Coma)
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  telefonosList: string[] = [];
  celularesList: string[] = [];
  inputTelefonoCelular: string = '';

  // Propiedades para autocompletado de calle
  calleControl = new FormControl('');
  calleSuggestions: any[] = [];
  private calleSearchTerms = new Subject<string>();

  // Almacena la última sugerencia seleccionada para usarla en la búsqueda
  selectedAddressFeature: any = null;

  @ViewChild(MapComponent) mapComponent: MapComponent;

  constructor(
    private signoCentroMedicoService: SignoCentroMedicoService,
    private catalogoService: CatalogoService,
    private signoLocalidadService: SignoLocalidadService,
    private signoPlanService: SignoPlanService,
    private router: Router,
    private guiMsjService: GuiMsjService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    // Configurar búsqueda con debounce para la calle
    this.calleSearchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => this.searchCalleSuggestions(term))
    ).subscribe(suggestions => {
      this.calleSuggestions = suggestions;
    });
  }

  async ngOnInit(): Promise<void> {
    // 1. Cargar todos los datos maestros y el centro médico en paralelo
    await this.cargarDatosIniciales();

    // 2. Inicializar los arrays de chips con objetos completos (concepto, etc.)
    this.inicializarChips();

    // 3. Crear el formulario (ahora ya tenemos todos los datos)
    this.createForm();

    // 4. Sincronizar calleControl con el valor existente del formulario
    this.calleControl.setValue(this.form.get('calle')?.value || '');

    // 5. Inicializar los filtros de autocomplete para los chips
    this.initAutocompleteFilters();

    // 6. Forzar detección de cambios por si acaso
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.updateMap();
  }

  // ─── Getters de validación ───
  get centroMedicoNoValido() {
    return this.form.get('nombre')?.invalid && this.form.get('nombre')?.touched;
  }

  get calleNoValida() {
    return this.form.get('calle')?.invalid && this.form.get('calle')?.touched;
  }

  get alturaNoValida() {
    return this.form.get('altura')?.invalid && this.form.get('altura')?.touched;
  }

  get localidadNoValida() {
    return this.form.get('localidad')?.invalid && this.form.get('localidad')?.touched;
  }

  get zonaNoValida() {
    return this.form.get('zona')?.invalid && this.form.get('zona')?.touched;
  }

  // ─── Carga de datos asíncrona (Promise.all) ───
  private async cargarDatosIniciales() {
    try {
      const [especialidades, rubros, zonas, localidades, planes, centroMedico] = await Promise.all([
        this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES),
        this.catalogoService.findDescendantsByKey(EntidadesKeys.RUBROS),
        this.catalogoService.findDescendantsByKey(EntidadesKeys.ZONAS),
        this.signoLocalidadService.getLocalidades(),
        this.signoPlanService.getPlanes(),
        this.signoCentroMedicoService.getCentroMedicoSeleccionado()
      ]);

      this.especialidades = especialidades || [];
      this.rubros = rubros || [];
      this.zonas = zonas || [];
      this.localidades = localidades || [];
      this.planes = planes || [];
      this.signoCentroMedico = centroMedico;

      if (!this.signoCentroMedico) {
        await this.router.navigateByUrl('/centros-medicos-listado');
        return;
      }

      // Procesar teléfonos almacenados
      this.telefonosList = this.signoCentroMedico?.telefonos
        ? this.signoCentroMedico.telefonos.split(',').map(s => s.trim())
        : [];
      this.celularesList = this.signoCentroMedico?.celular
        ? this.signoCentroMedico.celular.split(',').map(s => s.trim())
        : [];

    } catch (error) {
      console.error("Error cargando datos iniciales", error);
    }
  }

  // ─── Inicialización de chips con objetos completos ───
  private inicializarChips() {
    // Especialidades
    const idsEspecialidades = this.signoCentroMedico?.especialidades
      ? this.signoCentroMedico.especialidades.map((e: any) => e._id || e)
      : [];
    this.especialidadesSeleccionadas = idsEspecialidades
      .map((id: string) => this.especialidades.find(e => e._id === id))
      .filter(Boolean) as Catalogo[];

    // Rubros
    const idsRubros = this.signoCentroMedico?.rubros
      ? this.signoCentroMedico.rubros.map((r: any) => r._id || r)
      : [];
    this.rubrosSeleccionados = idsRubros
      .map((id: string) => this.rubros.find(r => r._id === id))
      .filter(Boolean) as Catalogo[];

    // Inicializar también las listas filtradas para los autocompletes
    this.filteredEspecialidades = this.especialidades
      .filter(e => !this.especialidadesSeleccionadas.some(sel => sel._id === e._id));
    this.filteredRubros = this.rubros
      .filter(r => !this.rubrosSeleccionados.some(sel => sel._id === r._id));
  }

  // ─── Filtros para los autocompletes ───
  private initAutocompleteFilters() {
    this.especialidadControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe(value => {
      this.filterEspecialidades(typeof value === 'string' ? value : '');
    });

    this.rubroControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe(value => {
      this.filterRubros(typeof value === 'string' ? value : '');
    });
  }

  // ─── Manejo de chips de especialidades ───
  addEspecialidad(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) return;

    const especialidad = this.especialidades.find(e => e.concepto.toLowerCase() === value.toLowerCase());
    if (especialidad && !this.especialidadesSeleccionadas.some(e => e._id === especialidad._id)) {
      this.especialidadesSeleccionadas.push(especialidad);
      this.updateFormEspecialidades();
    }

    // Limpiar input
    const input = event.input;
    if (input) {
      input.value = '';
    }
    this.especialidadControl.setValue('');
  }

  selectedEspecialidad(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value;
    const especialidad = this.especialidades.find(e => e._id === id);
    if (especialidad && !this.especialidadesSeleccionadas.some(e => e._id === id)) {
      this.especialidadesSeleccionadas.push(especialidad);
      this.updateFormEspecialidades();
    }
    this.especialidadControl.setValue('');
  }

  removeEspecialidad(id: string | undefined): void {
    if (!id) return;
    const index = this.especialidadesSeleccionadas.findIndex(e => e._id === id);
    if (index >= 0) {
      this.especialidadesSeleccionadas.splice(index, 1);
      this.updateFormEspecialidades();
    }
  }

  private updateFormEspecialidades(): void {
    const ids = this.especialidadesSeleccionadas.map(e => e._id);
    this.form.patchValue({ especialidades: ids });
    this.filterEspecialidades(this.especialidadControl.value || '');
  }

  filterEspecialidades(query: string): void {
    const filterValue = query.toLowerCase();
    this.filteredEspecialidades = this.especialidades.filter(especialidad =>
      especialidad.concepto.toLowerCase().includes(filterValue) &&
      !this.especialidadesSeleccionadas.some(sel => sel._id === especialidad._id)
    );
  }

  // ─── Manejo de chips de rubros ───
  addRubro(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) return;

    const rubro = this.rubros.find(r => r.concepto.toLowerCase() === value.toLowerCase());
    if (rubro && !this.rubrosSeleccionados.some(r => r._id === rubro._id)) {
      this.rubrosSeleccionados.push(rubro);
      this.updateFormRubros();
    }

    const input = event.input;
    if (input) {
      input.value = '';
    }
    this.rubroControl.setValue('');
  }

  selectedRubro(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value;
    const rubro = this.rubros.find(r => r._id === id);
    if (rubro && !this.rubrosSeleccionados.some(r => r._id === id)) {
      this.rubrosSeleccionados.push(rubro);
      this.updateFormRubros();
    }
    this.rubroControl.setValue('');
  }

  removeRubro(id: string | undefined): void {
    if (!id) return;
    const index = this.rubrosSeleccionados.findIndex(r => r._id === id);
    if (index >= 0) {
      this.rubrosSeleccionados.splice(index, 1);
      this.updateFormRubros();
    }
  }

  private updateFormRubros(): void {
    const ids = this.rubrosSeleccionados.map(r => r._id);
    this.form.patchValue({ rubros: ids });
    this.filterRubros(this.rubroControl.value || '');
  }

  filterRubros(query: string): void {
    const filterValue = query.toLowerCase();
    this.filteredRubros = this.rubros.filter(rubro =>
      rubro.concepto.toLowerCase().includes(filterValue) &&
      !this.rubrosSeleccionados.some(sel => sel._id === rubro._id)
    );
  }

  // ─── Gestión de teléfonos/celulares ───
  agregarTelefonoCelular(tipo: 'telefono' | 'celular') {
    const valor = this.inputTelefonoCelular.trim();
    if (valor) {
      if (tipo === 'telefono') {
        this.telefonosList.push(valor);
      } else {
        this.celularesList.push(valor);
      }
      this.inputTelefonoCelular = '';
      this.form.get('telefonos')?.markAsTouched();
      this.form.get('celular')?.markAsTouched();
      this.form.updateValueAndValidity();
    }
  }

  eliminarTelefono(index: number) {
    this.telefonosList.splice(index, 1);
    this.form.get('telefonos')?.markAsTouched();
    this.form.updateValueAndValidity();
  }

  eliminarCelular(index: number) {
    this.celularesList.splice(index, 1);
    this.form.get('celular')?.markAsTouched();
    this.form.updateValueAndValidity();
  }

  // ─── Autocompletado de calle ───
  filterCalleKeydown(event: KeyboardEvent) {
    // Permite números y texto en la calle
  }

  onCalleInput() {
    const value = this.calleControl.value || '';
    this.form.patchValue({ calle: value });
    this.calleSearchTerms.next(value);
  }

  private searchCalleSuggestions(query: string): Promise<any[]> {
    if (!query || query.length < 3) {
      return Promise.resolve([]);
    }
    const token = environment.mapBoxToken;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&autocomplete=true&types=address&country=ar&limit=5`;
    return fetch(url)
      .then(response => response.json())
      .then(data => data.features || [])
      .catch(error => {
        console.error('Error fetching suggestions', error);
        return [];
      });
  }

  onCalleSelected(event: MatAutocompleteSelectedEvent) {
    const feature = event.option.value;
    if (!feature) return;

    this.selectedAddressFeature = feature;

    const calle = this.displayCalleFn(feature);

    this.form.patchValue({ calle });
    setTimeout(() => {
      this.calleControl.setValue(calle, { emitEvent: false });
    }, 0);

    if (this.form.get('altura')?.value) {
      this.buscarDireccionCompleta();
    }
  }

  displayCalleFn = (suggestion: any): string => {
    if (!suggestion) return '';
    if (typeof suggestion === 'string') return suggestion;
    if (suggestion.text) return suggestion.text;
    const primeraParte = suggestion.place_name ? suggestion.place_name.split(',')[0].trim() : '';
    const partes = primeraParte.split(' ');
    const ultimo = partes[partes.length - 1];
    if (/^\d+$/.test(ultimo) && partes.length > 1) {
      return partes.slice(0, -1).join(' ');
    }
    return primeraParte;
  };

  buscarDireccionCompleta() {
    const calle = this.form.get('calle')?.value;
    const altura = this.form.get('altura')?.value;
    if (!calle || !altura) {
      alert('Debe ingresar calle y altura para buscar.');
      return;
    }

    if (this.selectedAddressFeature) {
      if (this.selectedAddressFeature.center && this.selectedAddressFeature.center.length === 2) {
        const [lng, lat] = this.selectedAddressFeature.center;
        this.mapComponent.setLocation(lat, lng);
        this.form.patchValue({ latitud: lat, longitud: lng });
        return;
      }

      let localidad = '';
      if (this.selectedAddressFeature.context) {
        const placeContext = this.selectedAddressFeature.context.find((ctx: any) => ctx.id.startsWith('place.'));
        if (placeContext) {
          localidad = placeContext.text;
        }
      }

      const direccionCompleta = localidad
        ? `${calle} ${altura}, ${localidad}, Argentina`
        : `${calle} ${altura}, Argentina`;

      this.mapComponent.searchFromAddress(direccionCompleta)
        .then(() => console.log('Mapa actualizado'))
        .catch(error => {
          console.error('Error al buscar dirección:', error);
          alert('No se pudo encontrar la dirección.');
        });
    } else {
      const direccionCompleta = `${calle} ${altura}, Argentina`;
      this.mapComponent.searchFromAddress(direccionCompleta)
        .then(() => console.log('Mapa actualizado'))
        .catch(error => {
          console.error('Error al buscar dirección:', error);
          alert('No se pudo encontrar la dirección.');
        });
    }
  }

  // ─── Mapa ───
  initMap() {
    if (!isNaN(this.form.value.latitud) && !isNaN(this.form.value.longitud)) {
      this.mapComponent.setLocation(this.form.value.latitud, this.form.value.longitud);
    }
  }

  updateMap() {
    this.updatesCoordinates();
    if (!isNaN(this.form.value.latitud) && !isNaN(this.form.value.longitud)) {
      this.mapComponent.setLocation(this.form.value.latitud, this.form.value.longitud);
    }
  }

  updatesCoordinates() {
    this.mapComponent.coordinatesChanged.subscribe((coordinates) => {
      this.form.patchValue({
        latitud: coordinates.latitud,
        longitud: coordinates.longitud
      });
    });
  }

  updateMarkerLocation() {
    const latitud = this.form.get('latitud')?.value;
    const longitud = this.form.get('longitud')?.value;
    if (this.mapComponent && !isNaN(latitud) && !isNaN(longitud)) {
      this.mapComponent.setLocation(latitud, longitud);
    }
  }

  // ─── Formulario ───
  createForm() {
    const extraerIds = (arr: any[]): string[] => {
      if (!arr) return [];
      return arr.map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item._id) return item._id;
        return null;
      }).filter(id => id !== null);
    };

    this.form = new FormGroup({
      nombre: new FormControl(this.signoCentroMedico?.nombre, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(250),
      ]),
      activo: new FormControl(this.signoCentroMedico?.activo),
      calle: new FormControl(this.signoCentroMedico?.calle, [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
      ]),
      altura: new FormControl(this.signoCentroMedico?.altura, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(50),
      ]),
      pisoDpto: new FormControl(this.signoCentroMedico?.pisoDpto, [
        Validators.minLength(1),
        Validators.maxLength(250),
      ]),
      telefonos: new FormControl(''),
      celular: new FormControl(''),
      horarios: new FormControl(this.signoCentroMedico?.horarios, [
        Validators.minLength(1),
        Validators.maxLength(250),
      ]),
      zona: new FormControl(this.signoCentroMedico?.zona?._id || this.signoCentroMedico?.zona || '', [
        Validators.required,
      ]),
      localidad: new FormControl(this.signoCentroMedico?.localidad?._id || this.signoCentroMedico?.localidad || '', [
        Validators.required,
      ]),
      latitud: new FormControl(this.signoCentroMedico?.latitud, [
        Validators.pattern(/^-?\d*(\.\d+)?$/),
      ]),
      longitud: new FormControl(this.signoCentroMedico?.longitud, [
        Validators.pattern(/^-?\d*(\.\d+)?$/),
      ]),
      rubros: new FormControl(extraerIds(this.rubrosSeleccionados)),
      especialidades: new FormControl(extraerIds(this.especialidadesSeleccionadas)),
      planes: new FormControl(extraerIds(this.signoCentroMedico?.planes)),
    }, { validators: this.validarAlMenosUnContacto() });
  }

  validarAlMenosUnContacto(): ValidatorFn {
    return (): { [key: string]: any } | null => {
      if (this.telefonosList.length > 0 || this.celularesList.length > 0) {
        return null;
      }
      return { atLeastOneRequired: true };
    };
  }

  // ─── Envío del formulario ───
  async updateCentroMedico() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    await this.guiMsjService.msjFormSubmit('Guardando');
    const centroMedico = await this.setDataForma();
    this.signoCentroMedicoService.updateCentroMedico(this.signoCentroMedico._id, centroMedico);
    await this.guiMsjService.msjFormSubmit('DatosGuardados');
    this.router.navigateByUrl('/centros-medicos-listado');
  }

  async setDataForma(): Promise<SignoCentroMedico> {
    const telefonosConcatenados = this.telefonosList.join(', ');
    const celularesConcatenados = this.celularesList.join(', ');

    const especialidadesObjectId = this.form.value.especialidades.map((id: string) => ({ _id: id }));
    const rubrosObjectId = this.form.value.rubros.map((id: string) => ({ _id: id }));
    const localidad = await this.getLocalidadSeleccionada(this.form.value.localidad);
    const planesObjectId = this.form.value.planes.map((id: string) => ({ _id: id }));

    return {
      nombre: this.form.value.nombre || '',
      activo: this.form.value.activo || false,
      calle: this.form.value.calle || '',
      altura: this.form.value.altura || '',
      pisoDpto: this.form.value.pisoDpto || '',
      telefonos: telefonosConcatenados,
      celular: celularesConcatenados,
      zona: { _id: this.form.value.zona },
      localidad: localidad,
      horarios: this.form.value.horarios || '',
      longitud: Number(this.form.value.longitud) || 0.0,
      latitud: Number(this.form.value.latitud) || 0.0,
      rubros: rubrosObjectId,
      especialidades: especialidadesObjectId,
      planes: planesObjectId,
    };
  }

  async getLocalidadSeleccionada(_id: string): Promise<SignoLocalidad> {
    try {
      const localidad = await this.signoLocalidadService.getLocalidadById(_id);
      if (localidad) {
        return {
          _id: localidad._id,
          nombre: localidad.nombre,
          activo: localidad.activo,
          zona: localidad.zona
        };
      }
    } catch (error) {
      console.error("Error al obtener la localidad seleccionada", error);
    }
  }
}